import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateEntities } from 'redux-query';
import { useMutation } from 'redux-query-react';
import LayoutForm from 'automaailm-ui/lib/Layouts/LayoutForm';
import FormRow from 'automaailm-ui/lib/Components/FormRow';
import ControlInput from 'automaailm-ui/lib/Components/ControlInput';
import CheckboxWithLabel from 'automaailm-ui/lib/Components/CheckboxWithLabel';
import Button from 'automaailm-ui/lib/Components/Button';
import Buttons from 'automaailm-ui/lib/Components/Buttons';
import Callout from 'automaailm-ui/lib/Components/Callout';
import { useFormik } from 'formik';
import { displaySum } from 'components/Checkout/shared/common/functions';
import { detailsRules } from 'components/OrderReturns/ValidationRules';
import ControlTextarea from 'automaailm-ui/lib/Components/ControlTextarea';
import QuantityComponent from 'components/OrderReturns/Quantity';
import { IReturnOrder, IReturnProduct, ITranslations } from 'components/OrderReturns/ReturnForm';
import submitReturnRequest, {
    IReturnSubmitPayload,
    IReturnSubmitResponse,
} from 'data/requests/orderReturns/submitReturnRequest';
import { getCaptchaToken } from 'components/GoogleRecaptcha/Index';

interface IProps {
    labels: ITranslations;
    order: IReturnOrder;
    setSubmitted: (submitted: boolean) => void;
}

const OrderReturnSubmitForm = (props: IProps) => {
    const { labels, order, setSubmitted } = props;
    const dispatch = useDispatch();
    const [productsError, setProductsError] = useState(false);
    const [submitError, setSubmitError] = useState<string>();
    const [{ isPending }, sendReturn] = useMutation((payload: IReturnSubmitPayload, captchaToken: string) =>
        submitReturnRequest(payload, captchaToken),
    );
    const firstNameRef = useRef<ControlInput | null>(null);
    const isSubmitting = useRef(false);

    React.useEffect(() => {
        if (firstNameRef.current) {
            firstNameRef.current.focus();
        }
    }, []);

    const updateProduct = (id: string, changes: Partial<IReturnProduct>) => {
        dispatch(
            updateEntities<{ orderReturn: IReturnOrder }>({
                orderReturn: (prev) => ({
                    ...prev,
                    products: prev.products.map((product) =>
                        product.id === id ? { ...product, ...changes } : product,
                    ),
                }),
            }),
        );
    };

    const detailsForm = useFormik({
        initialValues: {
            firstName: order.customer.firstName,
            lastName: order.customer.lastName,
            reason: '',
            bankAccount: '',
            confirmed: false,
        },
        validationSchema: detailsRules(labels),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
            handleSubmit();
        },
    });

    const handleSubmit = () => {
        if (isSubmitting.current) {
            return;
        }

        const selected = order.products.filter((product) => product.selected);
        if (selected.length === 0) {
            setProductsError(true);
            return;
        }

        isSubmitting.current = true;
        setSubmitError(undefined);

        void getCaptchaToken()
            .catch(() => '')
            .then((captchaToken) =>
                sendReturn(
                    {
                        orderNumber: order.orderNumber,
                        email: order.customer.email,
                        phone: order.customer.phone,
                        firstName: detailsForm.values.firstName,
                        lastName: detailsForm.values.lastName,
                        reason: detailsForm.values.reason,
                        bankAccount: detailsForm.values.bankAccount,
                        confirmed: detailsForm.values.confirmed,
                        items: selected.map((product) => ({ id: product.id, qty: product.returnQuantity })),
                    },
                    captchaToken,
                ),
            )
            .then((response) => {
                const body = response?.body as IReturnSubmitResponse | undefined;
                if (body?.success) {
                    setSubmitted(true);
                    return;
                }
                setSubmitError(body?.error ?? labels.errorCheckData);
            })
            .catch(() => setSubmitError(labels.errorCheckData))
            .finally(() => {
                isSubmitting.current = false;
            });
    };

    const errorMessage = submitError ?? (productsError ? labels.errorSelectProduct : undefined);

    return (
        <React.Fragment>
            {' '}
            <Callout intent="success">
                {`${labels.orderLoadedPrefix} ${order.orderNumber} ${labels.orderLoadedSuffix}`}
            </Callout>
            <form onSubmit={detailsForm.handleSubmit}>
                <LayoutForm layout="vertical">
                    <FormRow label={labels.firstName} required={true} error={detailsForm.errors.firstName}>
                        <ControlInput
                            name="firstName"
                            ref={firstNameRef}
                            value={detailsForm.values.firstName}
                            hasError={!!detailsForm.errors.firstName}
                            onChange={detailsForm.handleChange}
                            onBlur={detailsForm.handleBlur}
                        />
                    </FormRow>
                    <FormRow label={labels.lastName} required={true} error={detailsForm.errors.lastName}>
                        <ControlInput
                            name="lastName"
                            value={detailsForm.values.lastName}
                            hasError={!!detailsForm.errors.lastName}
                            onChange={detailsForm.handleChange}
                            onBlur={detailsForm.handleBlur}
                        />
                    </FormRow>
                    <FormRow label={labels.reason} required={true} error={detailsForm.errors.reason}>
                        <ControlTextarea
                            name="reason"
                            value={detailsForm.values.reason}
                            minRows={3}
                            hasError={!!detailsForm.errors.reason}
                            onChange={detailsForm.handleChange}
                            onBlur={detailsForm.handleBlur}
                        />
                    </FormRow>
                    <FormRow label={labels.bankAccount} required={false}>
                        <ControlInput
                            name="bankAccount"
                            value={detailsForm.values.bankAccount}
                            placeholder={labels.bankAccountPlaceholder}
                            onChange={detailsForm.handleChange}
                        />
                    </FormRow>
                </LayoutForm>
                <h3>{labels.returnableProducts}</h3>
                <div className="table responsive returnform">
                    <table>
                        <thead>
                            <tr>
                                <th className="w1p">&nbsp;</th>
                                <th>{labels.columnProduct}</th>
                                <th className="center">{labels.columnQuantity}</th>
                                <th className="center">{labels.columnSum}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {order.products.map((product) => (
                                <tr key={product.id}>
                                    <td className="w1p">
                                        <input
                                            type="checkbox"
                                            checked={product.selected}
                                            onChange={() => {
                                                setProductsError(false);
                                                updateProduct(product.id, { selected: !product.selected });
                                            }}
                                        />
                                    </td>
                                    <td className="primary">{product.name}</td>
                                    <td className="center quantity">
                                        <QuantityComponent
                                            id={product.id}
                                            value={product.returnQuantity}
                                            min={1}
                                            max={product.orderedQuantity}
                                            ariaLabel={product.name}
                                            onChange={(value) => {
                                                updateProduct(product.id, { returnQuantity: value });
                                            }}
                                        />
                                    </td>
                                    <td className="center total">
                                        {displaySum(product.returnQuantity * product.price)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <LayoutForm layout="vertical">
                    <FormRow required={true}>
                        <CheckboxWithLabel
                            label={labels.confirmReturn}
                            checked={detailsForm.values.confirmed}
                            hasError={!!detailsForm.errors.confirmed}
                            onChange={() => detailsForm.setFieldValue('confirmed', !detailsForm.values.confirmed)}
                        />
                    </FormRow>
                </LayoutForm>

                {errorMessage && (
                    <Callout intent="danger">
                        <div>{errorMessage}</div>
                    </Callout>
                )}

                <Buttons layout="vertical-wide">
                    <Button
                        type="submit"
                        title={labels.submitReturn}
                        loading={isPending}
                        disabled={isPending}
                        intent="primary"
                    />
                </Buttons>
            </form>
        </React.Fragment>
    );
};
export default OrderReturnSubmitForm;
